'use client';

import { useCallback } from 'react';

import { useAnalytics } from '@/lib/analytics/use-analytics';
import { useGetFeatureFlagVariant } from '@/lib/feature-flags/hooks/use-get-feature-flag-variant';
import type { FeatureFlagVariant } from '@/lib/feature-flags/types';

import { useLabsArm } from './labs-arm';
import { usePack } from './pack-context';
import type { LabsEventName, VerticalPack } from './types';

interface UseLabsAnalytics {
  /** The experiment arm from the vertical's feature flag. Stamped on EVERY event. */
  arm: FeatureFlagVariant | undefined;
  isReady: boolean;
  track: (event: LabsEventName, props?: Record<string, unknown>) => void;
}

/**
 * Arm-stamped analytics for the active Labs vertical — the generic form of
 * `use-finance-discovery-analytics.ts`.
 *
 * Reads the pack's feature flag ONCE and exposes a `track` that only emits once the flag is resolved
 * (`isReady`), ALWAYS stamping `{ arm: variant, ...props }`. Emitting before the flag resolves would
 * tag events with the wrong arm, so we no-op until then. Raw `track()` must NOT be called outside
 * this hook (tracking-guard + BUGBOT) — every Labs event flows through here.
 *
 * THE ARM STAMP IS WHAT MAKES THE HOLDOUT WORK. Finance shipped with a single `experiment` variant,
 * so its read is only correlational (finishers self-select for engagement). A new vertical adds a
 * `control` variant to its flag and the causal read falls out of the SAME events, for free — every
 * event already carries the arm it was assigned.
 */
/**
 * The context-free core: arm-stamped analytics for an explicitly-passed vertical. Used by the entry
 * SURFACES that live OUTSIDE the flow's `PackProvider` — the side-nav rail and card — which have a pack
 * but no context. The in-flow {@link useLabsAnalytics} wraps this with the pack from context.
 */
export function useLabsAnalyticsForPack(pack: Pick<VerticalPack, 'featureFlag'>): UseLabsAnalytics {
  const { track: platformTrack } = useAnalytics();
  // `arm` is the CANONICAL, one-per-user arm that actually decides treatment (see labs-arm.ts).
  const { arm, isReady: armReady } = useLabsArm();
  // The vertical's own flag is still stamped, as `vertical_arm`, so the pre-2026-08 per-vertical series
  // stays interpretable and the migration to a single arm is auditable rather than silent.
  const { variant: verticalVariant, isReady: verticalReady } = useGetFeatureFlagVariant(pack.featureFlag);

  const isReady = armReady && verticalReady;

  const track = useCallback(
    (event: LabsEventName, props?: Record<string, unknown>): void => {
      if (!isReady) {
        return;
      }
      platformTrack(event, { arm, vertical_arm: verticalVariant, ...props });
    },
    [isReady, arm, verticalVariant, platformTrack],
  );

  return { arm, isReady, track };
}

export function useLabsAnalytics(): UseLabsAnalytics {
  const { featureFlag } = usePack();
  return useLabsAnalyticsForPack({ featureFlag });
}
